import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, Phone, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Club admin pending-approval queue.
 *
 * Lists every member who has a `pending` entry for THIS tenant in
 * `users.clubs[]`. Approving flips the status to `active` and the member can
 * sign in immediately. Denying removes the pending entry (the member account
 * itself isn't deleted — they may still belong to other clubs).
 */
export default function AdminPendingMembershipsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/admin/pending-memberships")
      .then((r) => setRows(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const decide = async (userId, decision, reason) => {
    setBusyId(userId);
    setError(null);
    try {
      await api.post(`/admin/pending-memberships/${userId}/decision`, { decision, reason: reason || null });
      // Optimistic: just remove the row instead of re-fetching.
      setRows((arr) => arr.filter((r) => r.user_id !== userId));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="admin-pending-memberships-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/admin" className="text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline">
          ← Back to Back Office
        </Link>
        <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Membership Requests</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              Pending Approvals
            </h1>
            <p className="text-sm text-[#4A4A4A] mt-3 max-w-2xl">
              Members who&rsquo;ve requested access to your club via the public invite link. Approve to grant immediate sign-in; deny to remove the request.
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            data-testid="pending-reload"
            className="btn-secondary text-xs"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="pending-error">
            {error}
          </div>
        )}

        <div className="mt-8 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Member</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Mobile</th>
                <th className="px-6 py-4">Requested</th>
                <th className="px-6 py-4 text-right">Decision</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-[#7A7A7A]" data-testid="pending-empty">
                  <Clock size={20} className="inline mr-2" />
                  No pending requests right now. New self-signups will appear here.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t border-[#E5E3D8]" data-testid={`pending-row-${r.user_id}`}>
                  <td className="px-6 py-4">
                    <div className="font-serif text-base text-[#1A1A1A]">{r.name || "—"}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {r.email}</span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {r.mobile_number
                      ? <span className="inline-flex items-center gap-1.5 font-mono"><Phone size={12} /> {r.mobile_number}</span>
                      : <span className="text-[#9A9A9A]">—</span>}
                  </td>
                  <td className="px-6 py-4 text-xs text-[#7A7A7A]">
                    {(r.requested_at || "").slice(0, 16).replace("T", " · ")}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => decide(r.user_id, "approve")}
                        disabled={busyId === r.user_id}
                        data-testid={`pending-approve-${r.user_id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-[#1A422D] underline underline-offset-4 hover:opacity-80"
                      >
                        <CheckCircle2 size={14} /> Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const reason = window.prompt("Optional: reason for denial (not shown to member)");
                          if (reason === null) return; // cancelled
                          decide(r.user_id, "deny", reason);
                        }}
                        disabled={busyId === r.user_id}
                        data-testid={`pending-deny-${r.user_id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-[#8a1a1a] underline underline-offset-4 hover:opacity-80"
                      >
                        <XCircle size={14} /> Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
